import { BookOpen, LogOut, Instagram, Menu, Shield, ChevronDown, ChevronLeft, ChevronRight, Database, Sparkles, User, Film, Users, MessageCircle, Download, Info } from "lucide-react";
import { useCallback, useState, useRef, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { StandardButton } from "./ui/standard-button";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useHapticFeedback } from "@/hooks/useHapticFeedback";
import { NotificationsDropdown } from "./NotificationsDropdown";
import { ProfileEditModal } from "./ProfileEditModal";
import { useInstallPrompt, type InstallMode } from "@/hooks/useInstallPrompt";
import { useIsMobile } from "@/hooks/use-mobile";
import leafMark from "@/assets/leaf-mark.png";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";

/** Tap-friendly PWA install button */
function PWAInstallButton({ mode, promptInstall, dismiss, appStoreUrl, haptic }: {
  mode: InstallMode;
  promptInstall: () => Promise<boolean>;
  dismiss: () => void;
  appStoreUrl: string;
  haptic: ReturnType<typeof useHapticFeedback>;
}) {
  const [showTip, setShowTip] = useState(false);

  const handleClick = async () => {
    haptic.impact.medium();
    if (mode === 'prompt') {
      await promptInstall();
      return;
    }
    if (mode === 'ios') {
      // Real iOS app beats a home-screen shortcut: widgets, App Store updates.
      window.open(appStoreUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    // macSafari — Add to Dock cannot be triggered from script, so explain it.
    setShowTip(prev => !prev);
  };

  // Add to Dock has two conditions that cannot be detected; unstated, the
  // instructions just look wrong to anyone they do not apply to.
  const message =
    'Open the File menu, then "Add to Dock". Not available in a Private window. Requires macOS Sonoma or later.';

  // One source of truth for what this control says. iOS and iPadOS share the
  // same App Store listing, so they must read identically; every other route
  // installs the web app and says so, rather than sharing the iOS wording.
  const copy: Record<Exclude<InstallMode, 'none'>, { label: string; aria: string }> = {
    ios: { label: 'iOS app', aria: 'Get the Leafnode iOS app on the App Store' },
    macSafari: { label: 'Install', aria: 'How to install the Leafnode app on macOS Safari' },
    prompt: { label: 'Install', aria: 'Install the Leafnode app on this device' },
  };
  const { label, aria } = copy[mode as Exclude<InstallMode, 'none'>] ?? copy.prompt;

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        className="flex items-center gap-1 h-8 px-1.5 text-slate-400 hover:text-blue-400 active:scale-95 transition-all rounded-lg"
        aria-label={aria}
        title={aria}
      >
        <Download className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
        <span className="text-[10px] leading-none whitespace-nowrap">{label}</span>
      </button>

      {showTip && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setShowTip(false)} />
          <div className="absolute top-full left-0 mt-2 w-56 p-3 rounded-lg bg-slate-800 border border-slate-600 shadow-xl z-[9999]">
            <p className="text-xs text-slate-200 leading-relaxed">{message}</p>
            <button
              onClick={() => { setShowTip(false); dismiss(); }}
              className="mt-2 text-[10px] text-slate-400 hover:text-slate-200"
            >
              Don't show again
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const Header = () => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { hasRole, profile, loading: profileLoading } = useProfile();
  const haptic = useHapticFeedback();
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const { mode, promptInstall, dismiss, appStoreUrl } = useInstallPrompt();
  const isMobile = useIsMobile();
  const navRef = useRef<HTMLElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    setCanScrollLeft(nav.scrollLeft > 2);
    setCanScrollRight(nav.scrollLeft < nav.scrollWidth - nav.clientWidth - 2);
  }, []);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    checkScroll();
    nav.addEventListener('scroll', checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(nav);
    return () => { nav.removeEventListener('scroll', checkScroll); ro.disconnect(); };
  }, [checkScroll]);

  const scrollNav = useCallback((dir: 'left' | 'right') => {
    const nav = navRef.current;
    if (!nav) return;
    nav.scrollBy({ left: dir === 'left' ? -160 : 160, behavior: 'smooth' });
  }, []);

  const handleNavKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') { scrollNav('right'); e.preventDefault(); }
    if (e.key === 'ArrowLeft') { scrollNav('left'); e.preventDefault(); }
  }, [scrollNav]);
  const userLabel =
    profile?.display_name?.trim() ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim() ||
    // While the profile loads, "Account": the id fallback flashed before the name.
    (user?.id && !profileLoading ? `User ${user.id.slice(0, 8)}` : 'Account');

  const handleMenuItemTap = useCallback(() => {
    haptic.selection();
  }, [haptic]);

  const handleSignOut = useCallback(() => {
    haptic.impact.medium();
    signOut();
  }, [haptic, signOut]);

  const handleOpenMenu = useCallback(() => {
    haptic.impact.light();
  }, [haptic]);

  const isAdminRoute = location.pathname.startsWith('/admin');

  // Admin single menu item (consolidated)
  const adminMenuItems = [
    { to: '/admin/enrichment', label: 'Data Enrichment', icon: Sparkles },
  ];
  
  return (
    <header className="bg-slate-900 pt-[env(safe-area-inset-top)]" role="banner" aria-label="Site header">
      <div className="w-full px-4 py-2 md:px-6 md:py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 md:gap-3">
            <Link 
              to="/" 
              className="flex items-center space-x-2 md:space-x-3 hover:opacity-80 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded-lg p-1"
              aria-label="Leafnode — for the future-literate, home"
            >
              <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center">
                <img src={leafMark} alt="" aria-hidden="true" className="w-full h-full object-contain" />
              </div>
              <div className="hidden sm:block">
                {/* The wordmark is the site logo, not the page's heading — a plain
                    element keeps each page's real <h1> (e.g. "About Leafnode") the
                    sole top-level heading, for a cleaner outline and screen-reader nav. */}
                <div className="text-lg md:text-xl font-light text-slate-200 tracking-wider">
                  LEAFNODE
                </div>
                <p className="text-xs text-slate-400 -mt-1">for the future-literate</p>
              </div>
            </Link>
            
            {/* Instagram icon - vertically centered with dot */}
            <a
              href="https://instagram.com/leafnode.scifi"
              target="_blank"
              rel="noopener noreferrer"
              className="lg:hidden flex items-center justify-center w-6 h-6 -ml-1 text-slate-400 hover:text-blue-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded"
              aria-label="Follow us on Instagram"
            >
              <Instagram className="w-4 h-4 flex-shrink-0" />
            </a>

            {/* PWA Install button - mobile only, when not yet installed */}
            {/* Not gated on isMobile any more: macOS Safari is a desktop
                browser with a real install route (File -> Add to Dock), and the
                mobile-only gate was a second reason it never saw a prompt. */}
            {mode !== 'none' && (
              <PWAInstallButton
                mode={mode}
                promptInstall={promptInstall}
                dismiss={dismiss}
                appStoreUrl={appStoreUrl}
                haptic={haptic}
              />
            )}
          </div>
          
          <div className="flex items-center gap-2 md:gap-6 min-w-0">
            {/* Instagram icon on desktop - stays in header right area */}
            <a
              href="https://instagram.com/leafnode.scifi"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:block text-slate-400 hover:text-blue-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded p-1"
              aria-label="Follow us on Instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
            
            {/* Scrollable nav with arrow indicators */}
            <div className="hidden lg:flex flex-1 min-w-0 items-center relative">
              {/* Left scroll arrow */}
              <button
                onClick={() => scrollNav('left')}
                className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:text-blue-400 transition-all duration-200 ${
                  canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                aria-label="Scroll navigation left"
                tabIndex={-1}
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>

              <nav
                ref={navRef}
                onKeyDown={handleNavKeyDown}
                className="flex flex-1 min-w-0 items-center gap-3 xl:gap-4 overflow-x-auto overscroll-x-contain scrollbar-hide smooth-scroll px-1"
                role="navigation"
                aria-label="Main navigation"
              >
              <Link
                to="/"
                className={`transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded px-1.5 py-1 whitespace-nowrap shrink-0 ${
                  location.pathname === '/' 
                    ? 'text-blue-400' 
                    : 'text-slate-300 hover:text-blue-400'
                }`}
              >
                Home
              </Link>
              <Link
                to="/library"
                className={`transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded px-1.5 py-1 whitespace-nowrap shrink-0 ${
                  location.pathname === '/library' 
                    ? 'text-blue-400' 
                    : 'text-slate-300 hover:text-blue-400'
                }`}
              >
                Transmissions
              </Link>
              <Link
                to="/book-browser"
                className={`transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded px-1.5 py-1 whitespace-nowrap shrink-0 ${
                  location.pathname === '/book-browser' 
                    ? 'text-blue-400' 
                    : 'text-slate-300 hover:text-blue-400'
                }`}
              >
                Signal Archive
              </Link>
              <Link
                to="/author-matrix"
                className={`transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded px-1.5 py-1 whitespace-nowrap shrink-0 ${
                  location.pathname === '/author-matrix' 
                    ? 'text-blue-400' 
                    : 'text-slate-300 hover:text-blue-400'
                }`}
              >
                Author Matrix
              </Link>
              <Link
                to="/insights"
                className={`transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded px-1.5 py-1 whitespace-nowrap shrink-0 ${
                  location.pathname === '/insights' 
                    ? 'text-blue-400' 
                    : 'text-slate-300 hover:text-blue-400'
                }`}
              >
                Reading Insights
              </Link>
              <Link
                to="/publisher-resonance"
                className={`transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded px-1.5 py-1 whitespace-nowrap shrink-0 ${
                  location.pathname === '/publisher-resonance' 
                    ? 'text-blue-400' 
                    : 'text-slate-300 hover:text-blue-400'
                }`}
              >
                Publisher Resonance
              </Link>
              <Link
                to="/test-brain"
                className={`transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded px-1.5 py-1 whitespace-nowrap shrink-0 ${
                  location.pathname === '/test-brain' 
                    ? 'text-blue-400' 
                    : 'text-slate-300 hover:text-blue-400'
                }`}
              >
                Neural Map
              </Link>
              <Link
                to="/community"
                className={`transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded px-1.5 py-1 whitespace-nowrap shrink-0 ${
                  location.pathname === '/community' 
                    ? 'text-emerald-400' 
                    : 'text-slate-300 hover:text-emerald-400'
                }`}
              >
                Community
              </Link>
              <Link
                to="/clubs"
                className={`transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded px-1.5 py-1 whitespace-nowrap shrink-0 ${
                  location.pathname.startsWith('/clubs')
                    ? 'text-emerald-400'
                    : 'text-slate-300 hover:text-emerald-400'
                }`}
              >
                Clubs
              </Link>
              <Link
                to="/protagonists"
                className={`transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded px-1.5 py-1 whitespace-nowrap shrink-0 flex items-center gap-1 ${
                  location.pathname === '/protagonists' 
                    ? 'text-violet-400' 
                    : 'text-slate-300 hover:text-violet-400'
                }`}
              >
                <MessageCircle className="w-3.5 h-3.5" />
                Protagonists
              </Link>
              <Link
                to="/book-to-screen"
                className={`transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded px-1.5 py-1 whitespace-nowrap shrink-0 flex items-center gap-1 ${
                  location.pathname === '/book-to-screen' 
                    ? 'text-amber-400' 
                    : 'text-slate-300 hover:text-amber-400'
                }`}
              >
                <Film className="w-3.5 h-3.5" />
                Book to Screen
              </Link>

              <Link
                to="/free-to-read"
                className={`transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded px-1.5 py-1 whitespace-nowrap shrink-0 flex items-center gap-1 ${
                  location.pathname === '/free-to-read'
                    ? 'text-amber-400'
                    : 'text-slate-300 hover:text-amber-400'
                }`}
              >
                <BookOpen className="w-3.5 h-3.5" />
                Free to Read
              </Link>

              {/* Desktop Admin Dropdown */}
              {hasRole('admin') && (
                <DropdownMenu open={adminMenuOpen} onOpenChange={setAdminMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded px-1.5 py-1 whitespace-nowrap shrink-0 flex items-center gap-1 ${
                        isAdminRoute 
                          ? 'text-amber-400' 
                          : 'text-amber-300/70 hover:text-amber-400'
                      }`}
                    >
                      <Shield className="w-3.5 h-3.5" />
                      Admin
                      <ChevronDown className={`w-3 h-3 transition-transform ${adminMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="end" 
                    className="bg-slate-900/95 backdrop-blur-xl border border-amber-500/20 shadow-2xl shadow-amber-500/10"
                  >
                    {adminMenuItems.map((item) => (
                      <DropdownMenuItem key={item.to} asChild>
                        <Link
                          to={item.to}
                          className={`flex items-center gap-2 px-3 py-2 cursor-pointer ${
                            location.pathname === item.to
                              ? 'text-amber-400'
                              : 'text-slate-200 hover:text-amber-400'
                          }`}
                        >
                          <item.icon className="w-4 h-4 text-amber-400/70" />
                          {item.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              </nav>

              {/* Right scroll arrow */}
              <button
                onClick={() => scrollNav('right')}
                className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:text-blue-400 transition-all duration-200 ${
                  canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                aria-label="Scroll navigation right"
                tabIndex={-1}
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {user ? (
              <div className="flex items-center space-x-1 md:space-x-3">
                <NotificationsDropdown />
                <button
                  onClick={() => setShowProfileEdit(true)}
                  className="hidden md:flex items-center gap-2 px-2 py-1 text-xs font-medium rounded-lg transition-all duration-200 bg-transparent border border-slate-600 text-slate-300 hover:border-blue-400 hover:text-blue-400"
                >
                  <User className="w-3 h-3" />
                  <span className="max-w-[140px] truncate">{userLabel}</span>
                </button>
                <StandardButton
                  onClick={signOut}
                  variant="standard"
                  size="sm"
                  className="flex items-center space-x-1"
                >
                  <LogOut className="w-3 h-3" />
                  <span className="hidden sm:inline">Sign Out</span>
                </StandardButton>
              </div>
            ) : (
              <Link
                to="/auth"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 border border-blue-400/30 text-blue-400 hover:bg-blue-400/10 hover:border-blue-400/50"
              >
                <User className="w-3 h-3" />
                <span>Sign In</span>
              </Link>
            )}

            {/* Profile Edit Modal */}
            <ProfileEditModal 
              isOpen={showProfileEdit} 
              onClose={() => setShowProfileEdit(false)} 
            />

            {/* Mobile Menu - Dropdown with scroll support */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  onClick={handleOpenMenu}
                  className="lg:hidden inline-flex items-center justify-center rounded p-2 text-slate-300 hover:text-blue-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                  aria-label="Open navigation menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                sideOffset={8} 
                className="z-[9999] min-w-56 max-h-[calc(100vh-6rem)] overflow-y-auto overscroll-contain bg-slate-900/95 backdrop-blur-xl border border-blue-500/20 shadow-2xl shadow-blue-500/10 [webkit-overflow-scrolling:touch]"
              >
                <DropdownMenuItem asChild>
                  <Link
                    to="/"
                    onClick={handleMenuItemTap}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer active:scale-[0.98] ${
                      location.pathname === '/'
                        ? 'text-blue-400'
                        : 'text-slate-200'
                    }`}
                  >
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-400/50" />
                    Home
                  </Link>
                </DropdownMenuItem>

                {/* Library Group */}
                <DropdownMenuSeparator className="bg-blue-500/10" />
                <div className="px-3 py-1">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Library</span>
                </div>

                <DropdownMenuItem asChild>
                  <Link
                    to="/library"
                    onClick={handleMenuItemTap}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer active:scale-[0.98] ${
                      location.pathname === '/library'
                        ? 'text-blue-400'
                        : 'text-slate-200'
                    }`}
                  >
                    <BookOpen className="w-4 h-4 text-blue-400/70" />
                    Transmissions
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link
                    to="/book-browser"
                    onClick={handleMenuItemTap}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer active:scale-[0.98] ${
                      location.pathname === '/book-browser'
                        ? 'text-blue-400'
                        : 'text-slate-200'
                    }`}
                  >
                    <Database className="w-4 h-4 text-blue-400/70" />
                    Signal Archive
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link
                    to="/insights"
                    onClick={handleMenuItemTap}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer active:scale-[0.98] ${
                      location.pathname === '/insights'
                        ? 'text-blue-400'
                        : 'text-slate-200'
                    }`}
                  >
                    <Sparkles className="w-4 h-4 text-blue-400/70" />
                    Reading Insights
                  </Link>
                </DropdownMenuItem>

                {/* Explore Group */}
                <DropdownMenuSeparator className="bg-blue-500/10" />
                <div className="px-3 py-1">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Explore</span>
                </div>

                <DropdownMenuItem asChild>
                  <Link
                    to="/author-matrix"
                    onClick={handleMenuItemTap}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer active:scale-[0.98] ${
                      location.pathname === '/author-matrix'
                        ? 'text-blue-400'
                        : 'text-slate-200'
                    }`}
                  >
                    <Users className="w-4 h-4 text-blue-400/70" />
                    Author Matrix
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link
                    to="/test-brain"
                    onClick={handleMenuItemTap}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer active:scale-[0.98] ${
                      location.pathname === '/test-brain'
                        ? 'text-blue-400'
                        : 'text-slate-200'
                    }`}
                  >
                    <div className="w-4 h-4 relative">
                      <div className="absolute inset-0 rounded-full bg-blue-400/20 animate-pulse" />
                      <div className="absolute inset-0.5 rounded-full bg-blue-400/40" />
                    </div>
                    Neural Map
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link
                    to="/publisher-resonance"
                    onClick={handleMenuItemTap}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer active:scale-[0.98] ${
                      location.pathname === '/publisher-resonance'
                        ? 'text-blue-400'
                        : 'text-slate-200'
                    }`}
                  >
                    <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-400/30 to-purple-400/30 border border-blue-400/50" />
                    Publisher Resonance
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link
                    to="/book-to-screen"
                    onClick={handleMenuItemTap}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer active:scale-[0.98] ${
                      location.pathname === '/book-to-screen'
                        ? 'text-amber-400'
                        : 'text-slate-200'
                    }`}
                  >
                    <Film className="w-4 h-4 text-amber-400/70" />
                    Book to Screen
                  </Link>
                </DropdownMenuItem>

                {/* Social Group */}
                <DropdownMenuSeparator className="bg-blue-500/10" />
                <div className="px-3 py-1">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Social</span>
                </div>

                <DropdownMenuItem asChild>
                  <Link
                    to="/protagonists"
                    onClick={handleMenuItemTap}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer active:scale-[0.98] ${
                      location.pathname === '/protagonists'
                        ? 'text-violet-400'
                        : 'text-slate-200'
                    }`}
                  >
                    <MessageCircle className="w-4 h-4 text-violet-400/70" />
                    Protagonists
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link
                    to="/community"
                    onClick={handleMenuItemTap}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer active:scale-[0.98] ${
                      location.pathname === '/community'
                        ? 'text-emerald-400'
                        : 'text-slate-200'
                    }`}
                  >
                    <Users className="w-4 h-4 text-emerald-400/70" />
                    Community
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link
                    to="/clubs"
                    onClick={handleMenuItemTap}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer active:scale-[0.98] ${
                      location.pathname.startsWith('/clubs')
                        ? 'text-emerald-400'
                        : 'text-slate-200'
                    }`}
                  >
                    <Users className="w-4 h-4 text-emerald-400/70" />
                    Clubs
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuSeparator className="bg-slate-700/50" />

                <DropdownMenuItem asChild>
                  <Link
                    to="/about"
                    onClick={handleMenuItemTap}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer active:scale-[0.98] ${
                      location.pathname === '/about'
                        ? 'text-blue-400'
                        : 'text-slate-300'
                    }`}
                  >
                    <Info className="w-4 h-4 text-blue-400/70" />
                    About
                  </Link>
                </DropdownMenuItem>

                {/* Mobile Admin — flat direct link, no sub-panel */}
                {hasRole('admin') && (
                  <>
                    <DropdownMenuSeparator className="bg-amber-500/20" />
                    <div className="px-3 py-1">
                      <span className="text-[10px] uppercase tracking-wider text-amber-500/60 font-medium">Admin</span>
                    </div>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/admin/enrichment"
                        onClick={handleMenuItemTap}
                        className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer active:scale-[0.98] ${
                          location.pathname === '/admin/enrichment'
                            ? 'text-amber-400'
                            : 'text-amber-300/70 hover:text-amber-400'
                        }`}
                      >
                        <Sparkles className="w-4 h-4 text-amber-400/70" />
                        Data Enrichment
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}

                {user && (
                  <>
                    <DropdownMenuSeparator className="bg-slate-700/50" />
                    <div className="px-3 py-2 text-xs text-slate-400 truncate">
                      {user.email}
                    </div>
                    <DropdownMenuItem 
                      onClick={handleSignOut}
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer text-slate-200 hover:text-red-400 active:scale-[0.98]"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
